import * as Axios from 'axios';
import BaseService from '@/services/common/BaseService';
import store from '@/store';

export interface IProductBlacklistEntry {
    prodId: string;
    orgId?: string;
    productType?: 'mortgage' | 'credit-card' | 'term-deposit';
    productName?: string;
    reason?: string;
    createdAt?: string;
    updatedAt?: string;
}

export default class ProductBlacklistService extends BaseService {
    // Get API base URL - use admin-server for deployed envs, bankwatch for local dev
    private get apiBase(): string {
        const env = process.env.VUE_APP_ENV || 'dev';

        // Local dev: use bankwatch API directly (different DynamoDB instance)
        if (env === 'dev') {
            return 'http://localhost:3100';
        }

        // Deployed envs: use admin-server API (shared DynamoDB)
        return store.state.apiBase;
    }

    // API path varies by environment
    private get apiPath(): string {
        const env = process.env.VUE_APP_ENV || 'dev';

        // Local dev: bankwatch API structure
        if (env === 'dev') {
            return '/api/v1/admin/product-blacklist';
        }

        // Deployed: admin-server API structure
        return '/bankwatch/product-blacklist';
    }

    public async getAllEntries(): Promise<IProductBlacklistEntry[]> {
        console.log('[ProductBlacklistService] Getting all entries...');
        console.log('[ProductBlacklistService] API Base:', this.apiBase);
        console.log('[ProductBlacklistService] Auth token exists:', !!store.state.$auth?.token);

        const apiConfig: Axios.AxiosRequestConfig = {
            method: 'get',
            url: `${this.apiBase}${this.apiPath}`,
            headers: { Authorization: `Bearer ${store.state.$auth.token}` }
        }

        console.log('[ProductBlacklistService] Making request to:', apiConfig.url);
        const res: Axios.AxiosResponse = await this.makeAxiosCall(apiConfig);
        console.log('[ProductBlacklistService] Response:', res.data);
        return res.data as IProductBlacklistEntry[];
    }

    public async getBlacklistedProductIds(): Promise<string[]> {
        const entries = await this.getAllEntries();
        return entries.map(e => e.prodId);
    }

    public async getEntry(prodId: string): Promise<IProductBlacklistEntry | null> {
        const apiConfig: Axios.AxiosRequestConfig = {
            method: 'get',
            url: `${this.apiBase}${this.apiPath}/${prodId}`,
            headers: { Authorization: `Bearer ${store.state.$auth.token}` }
        }

        const res: Axios.AxiosResponse = await this.makeAxiosCall(apiConfig);
        return res.data as IProductBlacklistEntry | null;
    }

    public async getEntriesByOrg(orgId: string): Promise<IProductBlacklistEntry[]> {
        const apiConfig: Axios.AxiosRequestConfig = {
            method: 'get',
            url: `${this.apiBase}${this.apiPath}/org/${orgId}`,
            headers: { Authorization: `Bearer ${store.state.$auth.token}` }
        }

        const res: Axios.AxiosResponse = await this.makeAxiosCall(apiConfig);
        return res.data as IProductBlacklistEntry[];
    }

    public async createEntry(entry: Omit<IProductBlacklistEntry, 'createdAt' | 'updatedAt'>): Promise<any> {
        const apiConfig: Axios.AxiosRequestConfig = {
            method: 'post',
            url: `${this.apiBase}${this.apiPath}`,
            data: entry,
            headers: { Authorization: `Bearer ${store.state.$auth.token}` }
        }

        const res: Axios.AxiosResponse = await this.makeAxiosCall(apiConfig);
        return res.data;
    }

    public async updateEntry(prodId: string, updates: Partial<IProductBlacklistEntry>): Promise<any> {
        // Note: Update not implemented in admin-server yet
        console.warn('Update functionality not implemented in admin-server');
        return null;
    }

    public async deleteEntry(prodId: string): Promise<void> {
        const apiConfig: Axios.AxiosRequestConfig = {
            method: 'delete',
            url: `${this.apiBase}${this.apiPath}/${prodId}`,
            headers: { Authorization: `Bearer ${store.state.$auth.token}` }
        }

        const res: Axios.AxiosResponse = await this.makeAxiosCall(apiConfig);
        return res.data;
    }
}
